import type { IdSeccion } from './geometriaTablero';
import { PARAMETRO_HIJO } from './hijos/tiposHijo';

// D-09 / AST-10: assistant state 2 keeps the left-hand route and only adds this flag, so the
// section or overview underneath survives a reload and Back replays the sidebar opening.
export const PARAMETRO_ASISTENTE = 'assistant';
export const VALOR_ASISTENTE_ABIERTO = 'open';
// D-63: el hilo que el asistente tiene a la vista, para recuperarlo tras una carga completa.
export const PARAMETRO_HILO_ASISTENTE = 'hilo';

const PREFIJO_PACIENTES = '/app/dashboard/patients';
// `list` is the only sibling of `[patientId]` under the patients route.
const SEGMENTOS_NO_PACIENTE: ReadonlySet<string> = new Set(['list']);

// D-09: true on the patient board and everything under it (sections, `/assistant/<fn>`),
// false on the map, the list and every other route. Query and trailing slash are ignored.
export function esRutaTableroPaciente(ruta: string | null): boolean {
    if (!ruta) return false;
    const [camino] = ruta.split(/[?#]/);
    if (!camino.startsWith(`${PREFIJO_PACIENTES}/`)) return false;
    const [segmento] = camino.slice(PREFIJO_PACIENTES.length + 1).split('/');
    return Boolean(segmento) && !SEGMENTOS_NO_PACIENTE.has(segmento);
}

// D-19/IN-03: the board's assistant answers from the real patient's threads
// (usePatientChat) before H2 resolves the real name; naming a made-up patient in the
// breadcrumbs and the assistant header would attribute real clinical answers to nobody, so
// both show this neutral label until the summary arrives.
export const ETIQUETA_PACIENTE_EJEMPLO = 'Paciente de ejemplo';

// D-08: section segments follow the app's English route convention.
export const SEGMENTO_DE_SECCION: Readonly<Record<IdSeccion, string>> = Object.freeze({
    sesiones: 'sessions',
    evidencias: 'evidence',
    documentos: 'documents',
    notas: 'notes',
    analisis: 'analysis',
    plan: 'plan',
});

// Single source for section labels: board cards, breadcrumbs (plan 01-06) and top bar.
export const ETIQUETA_SECCION: Readonly<Record<IdSeccion, string>> = Object.freeze({
    sesiones: 'Sesiones de audio',
    evidencias: 'Evidencias',
    documentos: 'Documentos',
    notas: 'Notas de progreso',
    analisis: 'Análisis diagnóstico',
    plan: 'Plan de tratamiento',
});

export const SECCION_DE_SEGMENTO: Readonly<Record<string, IdSeccion>> = Object.freeze(
    Object.fromEntries(
        Object.entries(SEGMENTO_DE_SECCION).map(([seccion, segmento]) => [segmento, seccion as IdSeccion]),
    ),
);

function rutaPaciente(pacienteId: string): string {
    if (!pacienteId) throw new Error('rutasTablero: se necesita un pacienteId para construir la ruta');
    return `${PREFIJO_PACIENTES}/${encodeURIComponent(pacienteId)}`;
}

// D-10: the old six-tab page is gone — `/app/dashboard/patients/[patientId]` is the board
// itself now, so this is the bare patient URL with no discriminator. Phase 6 links through
// this same name and signature (D-26).
export function rutaTablero(pacienteId: string): string {
    return rutaPaciente(pacienteId);
}

export function rutaSeccion(pacienteId: string, seccion: IdSeccion): string {
    return `${rutaPaciente(pacienteId)}/${SEGMENTO_DE_SECCION[seccion]}`;
}

// A fixed base only lets URL parse relative routes; it never reaches the result.
const BASE_RUTAS = 'http://tablero.local';

export function conParametros(ruta: string, cambiar: (parametros: URLSearchParams) => void): string {
    const url = new URL(ruta, BASE_RUTAS);
    cambiar(url.searchParams);
    const consulta = conDosPuntosLiteralesEnAbrir(url.searchParams.toString());
    return consulta ? `${url.pathname}?${consulta}` : url.pathname;
}

// F6 (D-07 de la fase 15): URLSearchParams codifica ':' como %3A. Es legal en una consulta, pero el contrato del hijo es el
// literal `abrir=<tipo>:<id>`, así que el valor de `abrir` (y solo ese) vuelve a llevar los dos puntos sin codificar.
// Un único serializador para todos los escritores: ninguno tiene que acordarse de deshacer la codificación.
const PATRON_ABRIR = new RegExp(`(^|&)(${PARAMETRO_HIJO}=)([^&]*)`, 'g');

function conDosPuntosLiteralesEnAbrir(consulta: string): string {
    return consulta.replace(PATRON_ABRIR, (_coincidencia, union: string, nombre: string, valor: string) => union + nombre + valor.replace(/%3A/gi, ':'));
}

// Keeps every other parameter so state 2 sits on top of exactly what the therapist was
// looking at.
export function conAsistenteAbierto(ruta: string): string {
    return conParametros(ruta, (parametros) => parametros.set(PARAMETRO_ASISTENTE, VALOR_ASISTENTE_ABIERTO));
}

// D-63: sin asistente tampoco queda hilo a la vista que recordar.
export function sinAsistente(ruta: string): string {
    return conParametros(ruta, (parametros) => {
        parametros.delete(PARAMETRO_ASISTENTE);
        parametros.delete(PARAMETRO_HILO_ASISTENTE);
    });
}

// D3-39/D-02 (08-12 Task 2): la sesión seleccionada del D3 timeline vive en la URL de la propia
// sección -- la única forma de enlazar dentro del tablero a una sesión concreta (los chips de D3
// y secciones futuras pasan por aquí, nunca componen `?sesion=` a mano).
export const PARAMETRO_SESION = 'sesion';

export function rutaSesionEnTablero(pacienteId: string, sesionId: string): string {
    return conParametros(rutaSeccion(pacienteId, 'sesiones'), (parametros) => {
        parametros.set(PARAMETRO_SESION, sesionId);
    });
}

// E4-33 / D9-43: el documento seleccionado de Documentos vive en la URL de su sección, igual que la
// sesión de D3; la cronología y cualquier enlace a un documento concreto pasan por aquí.
export const PARAMETRO_DOCUMENTO = 'documento';

export function rutaDocumentoEnTablero(pacienteId: string, documentoId: string): string {
    return conParametros(rutaSeccion(pacienteId, 'documentos'), (parametros) => {
        parametros.set(PARAMETRO_DOCUMENTO, documentoId);
    });
}

// D-23/D3-44 (08-14): «Preguntar al asistente sobre esta sesión» necesita su PROPIO parámetro --
// `?sesion=` ya es la selección de la propia sección (D-02), y el alcance del asistente es un
// concepto distinto (una sesión puede estar seleccionada en Sesiones sin que el asistente esté
// acotado a ella, y viceversa desde otra sección).
export const PARAMETRO_SESION_ASISTENTE = 'sesionAsistente';

export function conAsistenteSobreSesion(ruta: string, sesionId: string): string {
    return conParametros(conAsistenteAbierto(ruta), (parametros) => {
        parametros.set(PARAMETRO_SESION_ASISTENTE, sesionId);
    });
}

// D-63: el hilo a la vista viaja en la URL mientras el asistente está abierto; las dos funciones
// conservan el resto de parámetros.
export function conHilo(ruta: string, hiloId: string): string {
    return conParametros(ruta, (parametros) => parametros.set(PARAMETRO_HILO_ASISTENTE, hiloId));
}

export function sinHilo(ruta: string): string {
    return conParametros(ruta, (parametros) => parametros.delete(PARAMETRO_HILO_ASISTENTE));
}
